import React, { useEffect, useRef, useState } from "react";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { createOrUpdateUserBillingRates } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { toast } from "react-toastify";
import { CurrencySymbol, UserType } from "../../../utils/enum";
import countries from "../../../countries.json";

const validationSchema = yup.object({
  currency: yup.string("Select currency").required("Currency is required"),
  HOURLY: yup.number().required("Hourly rate is required"),
  FULL_DAY: yup.number().required("Full Day rate is required"),
  HALF_DAY: yup.number().required("Half Day rate is required"),
});

const BillingRates = ({
  userBillingRates,
  userId,
  refetchMe,
  close,
  address,
  setIsAllowToSubmit,
  authRole,
  mode,
  onDirtyChange,
}) => {
  const dispatch = useDispatch();
  const [billingRates, setBillingRates] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const filteredCurrency = countries?.find((c) => c.country === address?.country)?.currency_code;
  const hasBillingData = userBillingRates && userBillingRates.length > 0;
  const {
    isPending: isUserBillingRatesLoading,
    mutateAsync: createOrUpdateUserBillingRatesMutation,
  } = useMutation({
    mutationFn: createOrUpdateUserBillingRates,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      currency: "AUD",
      HOURLY: "",
      FULL_DAY: "",
      HALF_DAY: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const newRates = [
        {
          paymentType: "HOURLY",
          currency: values.currency,
          rate: values.HOURLY,
        },
        {
          paymentType: "FULL_DAY",
          currency: values.currency,
          rate: values.FULL_DAY,
        },
        {
          paymentType: "HALF_DAY",
          currency: values.currency,
          rate: values.HALF_DAY,
        },
      ];

      try {
        setBillingRates(newRates);

        await createOrUpdateUserBillingRatesMutation({
          billingRates: newRates,
          userId,
        });

        dispatch(
          showToast({
            message: "Billing rates added successfully",
            type: "success",
          }),
        );

        refetchMe?.();
        close?.();
        setIsAllowToSubmit?.(true);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to save billing rates");
      }
    },
  });
  const prevCountryRef = useRef(address?.country || "");
  useEffect(() => {
    if (hasBillingData) {
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  }, [hasBillingData]);
  useEffect(() => {
    if (authRole === UserType.ADMIN) return;
    if (!address?.country) return;
    refetchMe();
    const newCurrency = countries.find((c) => c.country === address.country)?.currency_code;

    if (prevCountryRef.current !== address.country) {
      formik.setValues({
        currency: newCurrency || "",
        HOURLY: "",
        FULL_DAY: "",
        HALF_DAY: "",
      });
      const rates = [
        {
          paymentType: "HOURLY",
          currency: newCurrency || "",
          rate: 0,
        },
        {
          paymentType: "FULL_DAY",
          currency: newCurrency || "",
          rate: 0,
        },
        {
          paymentType: "HALF_DAY",
          currency: newCurrency,
          rate: 0,
        },
      ];
      createOrUpdateUserBillingRatesMutation({
        billingRates: rates,
        userId,
      });
      setIsAllowToSubmit(false);
    } else {
      if (newCurrency && formik.values.currency !== newCurrency) {
        formik.setFieldValue("currency", newCurrency);
      }
    }

    prevCountryRef.current = address.country;
  }, [address.country]);

  useEffect(() => {
    if (userBillingRates && userBillingRates.length > 0) {
      setBillingRates(userBillingRates);

      const firstCurrency = userBillingRates[0].currency;
      const ratesForCurrency = userBillingRates.reduce((acc, r) => {
        acc[r.paymentType] = r.rate;
        return acc;
      }, {});

      formik.setValues({
        currency: firstCurrency,
        HOURLY: ratesForCurrency.HOURLY || "",
        FULL_DAY: ratesForCurrency.FULL_DAY || "",
        HALF_DAY: ratesForCurrency.HALF_DAY || "",
      });
    }
  }, [userBillingRates]);

  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    formik.setValues({
      currency: newCurrency,
      HOURLY: "",
      FULL_DAY: "",
      HALF_DAY: "",
    });
  };
  const handleCancel = () => {
    if (userBillingRates && userBillingRates.length > 0) {
      const firstCurrency = userBillingRates[0].currency;

      const ratesForCurrency = userBillingRates.reduce((acc, r) => {
        acc[r.paymentType] = r.rate;
        return acc;
      }, {});

      formik.setValues({
        currency: firstCurrency,
        HOURLY: ratesForCurrency.HOURLY || "",
        FULL_DAY: ratesForCurrency.FULL_DAY || "",
        HALF_DAY: ratesForCurrency.HALF_DAY || "",
      });
    }

    setIsEditMode(false);
  };
  const allCurrencyOptions = Array.from(new Set(countries.map((c) => c.currency_code))).map(
    (code) => ({
      label: code,
      value: code,
    }),
  );

  const filteredCurrencyOption = filteredCurrency
    ? [{ label: filteredCurrency, value: filteredCurrency }]
    : [];

  const currencyOptions = authRole === UserType.ADMIN ? allCurrencyOptions : filteredCurrencyOption;
  const isReadOnly = mode !== "USER_SETTINGS" ? !isEditMode : false;

  // Unsaved edits: current rate inputs differ from what's already saved.
  const savedRates = (userBillingRates || []).reduce((acc, r) => {
    acc[r.paymentType] = String(r.rate ?? "");
    return acc;
  }, {});
  const savedCurrency = userBillingRates?.[0]?.currency || "";
  const isDirty =
    String(formik.values.HOURLY ?? "") !== (savedRates.HOURLY ?? "") ||
    String(formik.values.FULL_DAY ?? "") !== (savedRates.FULL_DAY ?? "") ||
    String(formik.values.HALF_DAY ?? "") !== (savedRates.HALF_DAY ?? "") ||
    (!!savedCurrency && formik.values.currency !== savedCurrency);

  // Form is saveable only once currency + all three rates are filled with valid values.
  const canSave =
    !!formik.values.currency &&
    Number(formik.values.HOURLY) > 0 &&
    Number(formik.values.HALF_DAY) > 0 &&
    Number(formik.values.FULL_DAY) > 0;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  return (
    <form onSubmit={formik.handleSubmit}>
      {mode !== "USER_SETTINGS" && hasBillingData && (
        <div className="flex justify-end gap-2 mb-3">
          {isEditMode && (
            <BaseButton color="primary" onClick={handleCancel} className="py-[5px] px-[38px]">
              Cancel
            </BaseButton>
          )}

          <BaseButton
            color="primary"
            onClick={() => setIsEditMode(true)}
            disabled={isEditMode}
            className={`py-[5px] px-[38px] ${
              isEditMode ? "bg-gray-400 text-white cursor-not-allowed" : ""
            }`}
          >
            Edit
          </BaseButton>
        </div>
      )}
      <section className="py-4 px-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 lg:gap-x-12 gap-y-4">
          <BaseAutoSelect
            labelShrink={false}
            label={
              <>
                Currency <span className="text-red-500 ml-1">*</span>
              </>
            }
            required={true}
            fullWidth
            options={currencyOptions}
            id="currency"
            name="currency"
            value={formik.values.currency}
            onChange={handleCurrencyChange}
            onBlur={formik.handleBlur}
            error={formik.touched.currency && Boolean(formik.errors.currency)}
            helperText={formik.touched.currency && formik.errors.currency}
            disabled={isReadOnly}
          />

          <BaseTextField
            optional={false}
            labelShrink={false}
            type="number"
            label={
              <>
                Hourly Rate <span className="text-red-500">*</span>
              </>
            }
            id="HOURLY"
            name="HOURLY"
            value={formik.values.HOURLY}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.HOURLY && Boolean(formik.errors.HOURLY)}
            helperText={formik.touched.HOURLY && formik.errors.HOURLY}
            disabled={isReadOnly}
          />

          <BaseTextField
            optional={false}
            labelShrink={false}
            type="number"
            label={
              <>
                Half Day Rate <span className="text-red-500">*</span>
              </>
            }
            id="HALF_DAY"
            name="HALF_DAY"
            value={formik.values.HALF_DAY}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.HALF_DAY && Boolean(formik.errors.HALF_DAY)}
            helperText={formik.touched.HALF_DAY && formik.errors.HALF_DAY}
            disabled={isReadOnly}
          />

          <BaseTextField
            optional={false}
            labelShrink={false}
            type="number"
            label={
              <>
                Full Day Rate <span className="text-red-500">*</span>
              </>
            }
            id="FULL_DAY"
            name="FULL_DAY"
            value={formik.values.FULL_DAY}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.FULL_DAY && Boolean(formik.errors.FULL_DAY)}
            helperText={formik.touched.FULL_DAY && formik.errors.FULL_DAY}
            disabled={isReadOnly}
          />
        </div>
        {billingRates?.length > 0 && (
          <div className="bg-light py-4 px-5 grid gap-6 rounded-md mt-6">
            <div className="bg-white px-4 py-2 rounded-md">
              <p className="font-semibold text-primary mb-2">{billingRates[0].currency}</p>
              {billingRates.map((rate) => (
                <div key={rate.paymentType} className="flex items-center px-2 py-1">
                  <p className="min-w-20 font-normal text-primary">
                    {rate.paymentType === "HOURLY"
                      ? "Hourly Rate"
                      : rate.paymentType === "FULL_DAY"
                        ? "Full Day"
                        : "Half Day"}
                  </p>
                  <div className="border-t border-dotted border-gray-500 w-full mx-2"></div>
                  <p className="min-w-16 text-right text-primary">
                    {rate.rate} {rate.currency}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isReadOnly && (
          <div className="flex flex-col items-end gap-1 mt-5">
            {isDirty && canSave && (
              <p className="text-amber-600 text-sm font-normal">
                You have unsaved changes — click Save to apply.
              </p>
            )}
            <BaseButton
              loaderSize={"22px"}
              isLoading={isUserBillingRatesLoading}
              disabled={isUserBillingRatesLoading || !canSave}
              color="primary"
              className="py-[5px] px-[38px]"
              type="submit"
            >
              Save
            </BaseButton>
          </div>
        )}
      </section>
    </form>
  );
};

export default BillingRates;
